import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import * as Styled from 'topBar/styles/TopBar.style';
import * as Buttons from 'topBar/styles/TopBarButtons.style';
import { TopBarBackButton } from 'topBar/components/TopBarBackButton';
import { TopBarTitle } from 'topBar/components/TopBarTitle';
import { SingleItemAction } from 'topBar/components/SingleItemAction';
import { MultiItemMenu } from 'topBar/components/MultiItemMenu';

export interface TopBarMenuItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string;
  icon?: React.ReactNode;
  successMessage?: string;
  selectedStyling?: boolean;
  primary?: boolean;
  destructive?: boolean;
  delayBeforeClose?: number;
  subItems?: TopBarMenuItem[];
  subHeaderLabel?: string;
}

export interface TopBarIconAction {
  icon: React.ReactNode;
  onClick: () => void;
  ariaLabel: string;
}

interface TopBarProps {
  onBackClick?: () => void;
  menuItems?: TopBarMenuItem[];
  iconActions?: TopBarIconAction[];
  leftIconActions?: TopBarIconAction[];
  showBackButton?: boolean;
  hideBackButtonOnDesktop?: boolean;
  showOnDesktop?: boolean;
  backButtonAriaLabel?: string;
  backButtonIcon?: React.ReactNode;
  menuButtonAriaLabel?: string;
  title?: string;
  subtitle?: string;
  showTitle?: boolean;
  centerSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
}

export function TopBar({
  onBackClick,
  menuItems = [],
  iconActions = [],
  leftIconActions = [],
  showBackButton = true,
  hideBackButtonOnDesktop = false,
  showOnDesktop = false,
  backButtonAriaLabel = 'Back',
  backButtonIcon,
  menuButtonAriaLabel = 'Open menu',
  title,
  subtitle,
  showTitle = false,
  centerSlot,
  rightSlot,
}: TopBarProps): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const isSingleItem = menuItems.length === 1;

  const openSidePanel = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('showpanel', 'true');
    history.replace({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }, [history, location]);

  return (
    <Styled.Container
      $showOnDesktop={showOnDesktop}
      $hasCenterSlot={!!centerSlot}
    >
      <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: 8 }}>
        {showBackButton && (
          <TopBarBackButton
            onClick={onBackClick || openSidePanel}
            ariaLabel={backButtonAriaLabel}
            hideOnDesktop={hideBackButtonOnDesktop}
            icon={backButtonIcon}
          />
        )}
        {leftIconActions.length > 0 && (
          <Styled.ActionsRow>
            {leftIconActions.map((action) => (
              <Buttons.IconButton
                key={action.ariaLabel}
                onClick={action.onClick}
                aria-label={action.ariaLabel}
              >
                {action.icon}
              </Buttons.IconButton>
            ))}
          </Styled.ActionsRow>
        )}
      </div>

      {centerSlot ? (
        <Styled.CenterSlot>{centerSlot}</Styled.CenterSlot>
      ) : (
        (title || subtitle) && (
          <TopBarTitle title={title} subtitle={subtitle} visible={showTitle} />
        )
      )}

      {rightSlot ? (
        <Styled.ActionsRow>{rightSlot}</Styled.ActionsRow>
      ) : (
        (iconActions.length > 0 || menuItems.length > 0) && (
          <Styled.ActionsRow>
            {iconActions.map((action) => (
              <Buttons.IconButton
                key={action.ariaLabel}
                onClick={action.onClick}
                aria-label={action.ariaLabel}
              >
                {action.icon}
              </Buttons.IconButton>
            ))}
            {menuItems.length > 0 &&
              (isSingleItem ? (
                <SingleItemAction
                  item={menuItems[0]}
                  menuButtonAriaLabel={menuButtonAriaLabel}
                />
              ) : (
                <MultiItemMenu
                  items={menuItems}
                  menuButtonAriaLabel={menuButtonAriaLabel}
                />
              ))}
          </Styled.ActionsRow>
        )
      )}
    </Styled.Container>
  );
}
